import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Users, Briefcase, Clock3, Award } from "lucide-react";
import { SITE } from "@/config";
import { MaskedLine, Fade } from "@/components/Reveal";
import { PrimaryCTA, GhostCTA } from "@/components/CTA";

// Placeholder modificabili: i numeri reali verranno forniti e pubblicati qui.
const KPIS = [
  { icon: Users, label: "Aziende affiancate", value: "+200" },
  { icon: Briefcase, label: "Progetti realizzati", value: "+85" },
  { icon: Clock3, label: "Ore di consulenza", value: "+5.000" },
  { icon: Award, label: "Eventi e formazione", value: "+60" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden bg-ink pt-[72px] text-white"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_72%_28%,rgba(212,175,55,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(245,243,238,0.05)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:radial-gradient(70%_70%_at_72%_38%,black,transparent)]" />
        <div className="absolute -right-40 top-[6%] hidden h-[620px] w-[620px] rounded-full border border-accent/15 lg:block" />
        <div className="absolute -right-16 top-[16%] hidden h-[430px] w-[430px] animate-[spin_90s_linear_infinite] rounded-full border border-dashed border-accent/20 lg:block" />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-[1440px] gap-16 px-6 py-20 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ opacity: fade }} className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-8 font-mono text-[11px] uppercase tracking-[0.35em] text-accent"
          >
            Consulente Aziendale
          </motion.p>

          <h1 className="font-display text-[clamp(2.6rem,7.2vw,5.8rem)] font-extrabold leading-[1.02] tracking-tighter">
            <MaskedLine index={0}>
              Le aziende <span className="text-accent">crescono</span>
            </MaskedLine>
            <MaskedLine index={1}>quando crescono</MaskedLine>
            <MaskedLine index={2}>
              le <span className="text-accent">persone</span> che le guidano.
            </MaskedLine>
          </h1>

          <Fade delay={0.85} className="mt-10 max-w-xl">
            <p className="text-base leading-relaxed text-white/60 md:text-lg">
              Affianco imprenditori e aziende nel miglioramento delle persone, dell'organizzazione,
              dei numeri e del marketing per costruire aziende più forti, autonome e libere.
            </p>
          </Fade>

          <Fade delay={1} className="mt-12">
            <div className="flex flex-wrap items-center gap-4">
              <PrimaryCTA href={SITE.bf360} testid="hero-cta-primary" />
              <GhostCTA to="/#metodo" dark testid="hero-cta-secondary">
                Scopri il mio approccio
              </GhostCTA>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
              Fai gratuitamente il Business Freedom 360.
            </p>
          </Fade>

          <Fade delay={1.15} className="mt-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/35">
              Persone. Strategia. Organizzazione. Crescita.
            </p>
          </Fade>
        </motion.div>

        <div className="hidden lg:col-span-5 lg:block">
          <div className="relative flex items-end justify-center self-end">
            <div className="absolute -inset-7 rounded-full border border-accent/20" aria-hidden="true" />
            <div className="absolute -inset-16 rounded-full border border-white/5" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_62%,rgba(212,175,55,0.10),transparent_62%)]"
              aria-hidden="true"
            />
            <img
              src="/assets/mattia-hero.png"
              alt="Mattia Cosimo Saracino — Consulente Aziendale"
              data-testid="hero-photo"
              className="relative w-full max-w-[500px] [mask-image:linear-gradient(to_bottom,black_90%,transparent_99%)]"
            />
          </div>
        </div>

        <div className="lg:hidden">
          <div className="relative">
            <div
              className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,rgba(212,175,55,0.08),transparent_65%)]"
              aria-hidden="true"
            />
            <img
              src="/assets/mattia-hero.png"
              alt="Mattia Cosimo Saracino — Consulente Aziendale"
              data-testid="hero-photo-mobile"
              className="relative mx-auto w-full max-w-[290px] [mask-image:linear-gradient(to_bottom,black_90%,transparent_99%)]"
            />
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-auto w-full border-t border-white/10" data-testid="kpi-strip">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 gap-x-6 gap-y-8 px-6 py-8 lg:grid-cols-4 lg:px-12">
          {KPIS.map((k) => (
            <div key={k.label} className="flex items-center gap-4">
              <k.icon className="h-6 w-6 shrink-0 text-accent" aria-hidden="true" />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/40">{k.label}</p>
                <p className="mt-1 bg-gradient-to-b from-[#E5C158] to-[#9F761A] bg-clip-text font-display text-3xl font-extrabold tracking-tighter text-transparent lg:text-4xl">
                  {k.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
